/**
 * useBreakevens — breakeven prices for the rows on screen, computed without
 * freezing the tab.
 *
 * 🔴 THE WHOLE POOL IS NOT AN OPTION. ~340 players × 8 solves × ~7ms is roughly
 * 19 seconds of blocked main thread. So:
 *
 *   • only the rows the panel is showing get computed, plus a lookahead, and
 *     the window grows as you scroll
 *   • results arrive progressively — a row reads "—" until its number lands,
 *     and typing or scrolling is never blocked
 *   • one solve per animation-frame-ish tick, so the UI keeps its thread
 *
 * 🔴 THE CACHE IS KEYED ON BOTH SHEETS. A PPG override changes the objective
 * and therefore every breakeven in the list; a price override changes what
 * every rival costs and therefore every breakeven too. A cache that watched
 * only one of them would serve confidently wrong numbers, which is worse than
 * serving none. Identity is enough to detect it: both sheets are replaced
 * wholesale on every edit, so an identity check is exact and O(1).
 *
 * A Web Worker is the next step if this still feels slow. It is not here yet
 * because the chunked version measured fine — see the report in the commit.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { breakevenFor } from "../../utils/planBreakeven";

/** Rows past the fold to compute ahead of the user. */
export const LOOKAHEAD = 25;
/** How many solves per tick. One keeps the thread responsive. */
const PER_TICK = 1;

export default function useBreakevens({
  rows, rosterSlots, pool, priceOf, valueOf, budget, limit, enabled = true,
}) {
  const [results, setResults] = useState(() => new Map());

  // The snapshot `drain` reads from, so an in-flight solve cannot mix inputs
  // from before and after an edit.
  const depsRef = useRef(null);

  // The work queue lives in a ref: it is drained by a timer, and re-creating it
  // through state would restart the drain on every result.
  const queueRef = useRef([]);
  const timerRef = useRef(null);
  const cacheRef = useRef(new Map());

  /**
   * 🔴 THE COMPARISON HAPPENS IN AN EFFECT, NOT DURING RENDER — never derive
   * state from a ref you mutate in the same pass.
   *
   * This previously computed a `stale` boolean during render and overwrote
   * depsRef right there. StrictMode double-invokes render: the first pass saw
   * the change and overwrote the ref, the second compared against the
   * overwritten ref and saw none — and the second pass is the one that commits.
   * Both effects keyed on that boolean, so the cache was never cleared, the
   * enqueue filter matched every key against the full stale cache and queued
   * nothing, and a row kept its pre-override number forever. Set Gibbs to 24.0
   * PPG and "up to $62" never moved.
   *
   * Mutating a ref inside an effect is legal and runs once per commit, so the
   * comparison sees the real previous value. Keyed on the actual dependencies
   * rather than on a derived flag, which is what makes it honest.
   *
   * Declared BEFORE the enqueue effect on purpose: effects run in order, so
   * this clears before that refills.
   */
  useEffect(() => {
    const prev = depsRef.current;
    const changed = !prev
      || prev.rosterSlots !== rosterSlots
      || prev.pool !== pool
      || prev.priceOf !== priceOf      // rebuilt when the price sheet changes
      || prev.valueOf !== valueOf      // rebuilt when the PPG sheet changes
      || prev.budget !== budget;
    if (!changed) return;
    depsRef.current = { rosterSlots, pool, priceOf, valueOf, budget };
    cacheRef.current = new Map();
    queueRef.current = [];
    setResults(new Map());
  }, [rosterSlots, pool, priceOf, valueOf, budget]);

  const drain = useCallback(() => {
    timerRef.current = null;
    const queue = queueRef.current;
    if (queue.length === 0) return;

    const done = [];
    for (let i = 0; i < PER_TICK && queue.length; i++) {
      const player = queue.shift();
      const key = `${player.name}-${player.pos}`;
      if (cacheRef.current.has(key)) continue;
      const { breakeven } = breakevenFor(player, {
        rosterSlots: depsRef.current.rosterSlots,
        pool: depsRef.current.pool,
        priceOf: depsRef.current.priceOf,
        valueOf: depsRef.current.valueOf,
        budget: depsRef.current.budget,
      });
      cacheRef.current.set(key, breakeven);
      done.push([key, breakeven]);
    }

    if (done.length) {
      setResults((prev) => {
        const next = new Map(prev);
        done.forEach(([k, v]) => next.set(k, v));
        return next;
      });
    }
    if (queue.length) timerRef.current = setTimeout(drain, 0);
  }, []);

  useEffect(() => {
    // depsRef is set by the invalidation effect above, which runs first.
    if (!enabled || !rows || rows.length === 0 || !depsRef.current) return undefined;
    const want = rows.slice(0, Math.max(0, limit) + LOOKAHEAD);
    queueRef.current = want
      .map((r) => r.player)
      .filter((p) => p && !cacheRef.current.has(`${p.name}-${p.pos}`));
    if (queueRef.current.length && !timerRef.current) {
      timerRef.current = setTimeout(drain, 0);
    }
    return () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };
    // The five inputs are dependencies here too, so a sheet edit re-queues the
    // window after the effect above has emptied the cache. `stale` is gone: a
    // boolean derived during render was the bug.
  }, [rows, limit, enabled, drain, rosterSlots, pool, priceOf, valueOf, budget]);

  return results;
}
